import React, { useCallback, useEffect, useMemo } from "react";
import Input from "../UI/Input";
import { Autocomplete } from "../UI/Autocomplete";
import { translate } from "../../utils/translations/translate";
import { Dropdown } from "../UI/Dropdown";
import { STATUS_OPTIONS, EMPLOYMENT_TYPE_OPTIONS } from "../../utils/constants";
import { Label } from "../UI/styles";
import { useAutoCompleteOptions } from "../../utils/hooks/useAutoCompleteOptions";
import { FETCH_ACCOUNTS_BY_QUERY_URL } from "../../utils/services/constants";
import { ResourceLabelContent } from "../User/ResourceLabelContent";
import { StyledCardSection } from "./styles";
const ProjectResourceDetails = ({ formik, isCreateMode, isReadOnlyMode }) => {
  const {
    resourceId,
    projectId,
    empType,
    managerName,
    projectName,
    resourceName,
    contractualPath,
    status,
  } = formik.values;
  const {
    isLoading: isResourceOptionsLoading,
    options: resourceDrpDwnOptions,
    searchKey: resourceKey,
    updateSearchKey: updateResourceKey,
  } = useAutoCompleteOptions(FETCH_ACCOUNTS_BY_QUERY_URL, "Admin=true&Approver=true&Employee=true&Search", !isCreateMode);
  const {
    isLoading: isManagerOptionsLoading,
    options: mngrDrpDwnOptions,
    searchKey: mngrKey,
    updateSearchKey: updateMngrKey,
  } = useAutoCompleteOptions(FETCH_ACCOUNTS_BY_QUERY_URL, "Admin=true&Approver=true&Search", isReadOnlyMode);
  const getFormattedOptions = useCallback((list) => {
    return list.map((option) => {
      return {
        label: option.ResourceName,
        secondaryLabel: `${option.Email} , ${option.AccountType}`,
        value: option.ResourceID,
        optionRef: option
      };
    });
  }, [])
  const formattedResourceDrpDwnOptions = useMemo(() => {
    const newOptions = getFormattedOptions(resourceDrpDwnOptions);
    return newOptions;
  }, [resourceDrpDwnOptions, getFormattedOptions]);
  const formattedMngrDrpDownOptions = useMemo(() => {
    const newOptions = getFormattedOptions(mngrDrpDwnOptions);
    return newOptions;
  }, [mngrDrpDwnOptions, getFormattedOptions]);
  useEffect(() => {
    if (!isCreateMode) {
      updateResourceKey({
        target: {
          value: resourceName
        }
      })
    }
  }, [resourceName, isCreateMode, updateResourceKey])
  useEffect(() => {
    if (!isReadOnlyMode) {
      updateMngrKey({
        target: {
          value: managerName
        }
      })
    }
  }, [managerName, isReadOnlyMode, updateMngrKey]);

  const disableInput = true;

  return (
    <StyledCardSection>
      <h1 className="tma_section-title">
        {translate("labels.resourceDetails")}
      </h1>

      <section className="tma-form_field_content two">
        <div className={`tma_form-control ${disableInput ? 'control-disabled' : ''}`}>
          <Input
            type="text"
            disabled={disableInput}
            id="resourceId"
            name="resourceId"
            value={resourceId}
            labelText={translate("labels.resourceId")}
            className='input-field'
          />
        </div>
        <div className={`tma_form-control  tma_autocomplete_control tma_align_label ${!isCreateMode ? 'control-disabled' : ''}`}>
          <Label>{translate("labels.resourceName")}</Label>
          <Autocomplete
            options={formattedResourceDrpDwnOptions}
            id="resourceName"
            name="resourceName"
            value={resourceName}
            disabled={!isCreateMode}
            autoCompleteValue={resourceKey}
            onSelection={(value) => {
              if (value === null) {
                formik.setFieldValue('resourceId', '');
                formik.setFieldValue("resourceName", '');
                updateResourceKey({ target: { value: '' } });
                return;
              }
              const { label, optionRef } = value;
              formik.setFieldValue('resourceId', optionRef.ResourceID);
              formik.setFieldValue("resourceName", label);
              updateResourceKey({ target: { value: label } });
            }}
            renderOption={(props, option) => {
              const { key, ...restItemProps } = props
              return <ResourceLabelContent itemProps={restItemProps} key={`${key}-${option.value}`} option={option} />
            }}
            onChange={updateResourceKey}
            onBlur={formik.handleBlur}
            loading={isResourceOptionsLoading}
            error={
              formik.touched.resourceName &&
              Boolean(formik.errors.resourceName)
            }
            helperText={
              formik.touched.resourceName && formik.errors.resourceName
            }
          />
        </div>
      </section>

      <section className="tma-form_field_content two">
        <div className={`tma_form-control ${disableInput ? 'control-disabled' : ''}`}>
          <Input
            id="tma_prj_id"
            name="tma_prj_id"
            disabled={disableInput}
            showInfoToolTip={false}
            placeholderLabelText={projectId ? '' : translate('labels.idInfoMsg')}
            labelText={translate("labels.projectId")}
            value={projectId}
          />
        </div>
        <div className="tma_form-control">
          <Input
            id="projectName"
            name="projectName"
            showInfoToolTip={false}
            inputProps={{ maxLength: 100 }}
            labelText={translate("labels.projectName")}
            value={projectName}
            disabled={isReadOnlyMode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.projectName && Boolean(formik.errors.projectName)
            }
            helperText={
              formik.touched.projectName && formik.errors.projectName
            }
          />
        </div>
      </section>

      <section className="tma-form_field_content two">
        <div className="tma_form-control tma_align_label">
          <Dropdown
            options={EMPLOYMENT_TYPE_OPTIONS}
            id="tma_prj_emp_type"
            label={translate("labels.employmentType")}
            disabled={isReadOnlyMode}
            initialValue={empType}
            key={empType}
            onChange={({ value }) => {
              formik.setFieldValue("empType", value);
            }}
          />
        </div>
        <div className="tma_form-control">
          <Input
            id="contractualPath"
            name="contractualPath"
            showInfoToolTip={false}
            labelText={translate("labels.contractPath")}
            value={contractualPath}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            inputProps={{ maxLength: 100 }}
            error={
              formik.touched.contractualPath &&
              Boolean(formik.errors.contractualPath)
            }
            helperText={
              formik.touched.contractualPath && formik.errors.contractualPath
            }
            disabled={isReadOnlyMode}
          />
        </div>
      </section>


      <section className="tma-form_field_content two">
        <div className="tma_form-control tma_autocomplete_control tma_align_label">
          <Label>{translate("labels.manager")}</Label>
          <Autocomplete
            options={formattedMngrDrpDownOptions}
            id="managerName"
            name="managerName"
            value={managerName}
            autoCompleteValue={mngrKey}
            disabled={isReadOnlyMode}
            onSelection={(value) => {
              if (value === null) {
                formik.setFieldValue('managerId', '');
                formik.setFieldValue("managerName", '');
                updateMngrKey({ target: { value: '' } });
                return;
              }
              const { label, optionRef } = value;
              formik.setFieldValue('managerId', optionRef.ResourceID);
              formik.setFieldValue("managerName", label);
              updateMngrKey({ target: { value: label } });
            }}
            renderOption={(props, option) => {
              const { key, ...restItemProps } = props
              return <ResourceLabelContent itemProps={restItemProps} key={`${key}-${option.value}`} option={option} />
            }}
            onChange={updateMngrKey}
            onBlur={formik.handleBlur}
            loading={isManagerOptionsLoading}
            error={
              formik.touched.managerName && Boolean(formik.errors.managerName)
            }
            helperText={
              formik.touched.managerName && formik.errors.managerName
            }
          />
        </div>
        <div className="tma_form-control">
          <Dropdown
            options={STATUS_OPTIONS}
            id="tma_prj_status_field"
            label={translate("labels.status")}
            initialValue={status}
            key={status}
            onChange={({ value }) => {
              formik.setFieldValue("status", value);
            }}
          />
        </div>
        <div className="tma_form-control"></div>
      </section>
    </StyledCardSection >
  );
};

export default ProjectResourceDetails;
